/**
 * @fileoverview Hook personalizado para gestión de categorías API
 * @module useCategoriesApi
 * @category Hook
 * @since 1.0.0
 * @version 1.0.0
 */
import { useState, useCallback } from 'react';
import fileUtils from '../util/fileUtils';
import { handleRequestErrors } from '../services/handleRequestError';

/**
 * Hook personalizado para todas las operaciones de categorías API.
 *
 * Abstrae todas las consultas y operaciones relacionadas con categorías,
 * proporcionando un estado centralizado y funciones para CRUD completo.
 *
 * @returns {Object} Estado y funciones para gestión de categorías
 */
const useCategoriesApi = () => {
  // Estados
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Obtiene categorías desde la API.
   *
   * @param {Object} params - Parámetros de consulta
   * @returns {Promise<Object>} Lista de categorías
   */
  const fetchCategories = useCallback(async (params = {}) => {
    const encodedParams = new URLSearchParams(params).toString();

    const endpoint = `${ENV.SHOPS_BASE_URL}/api/v1/retailers/categories?${encodedParams}`;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          Accept: 'application/json, text/plain',

          'MERCATELY-AUTH-TOKEN': ENV.MERCATELY_AUTH_TOKEN,

          'RETAILER-KEY': ENV.UNIQUE_KEY,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setCategories(data || []);

      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Crea una nueva categoría.
   *
   * @param {Object} body - Datos de la categoría
   * @returns {Promise<Object>} Categoría creada
   */
  const createCategory = useCallback(async (body) => {
    const endpoint = `${ENV.SHOPS_BASE_URL}/api/v1/retailers/categories`;
    const formData = fileUtils.objectToFormData({ category: body });

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {

          'MERCATELY-AUTH-TOKEN': ENV.MERCATELY_AUTH_TOKEN,

          'RETAILER-KEY': ENV.UNIQUE_KEY,
          Accept: 'application/json, text/plain, */*'
        },
        body: formData
      });

      const data = await handleRequestErrors(response);

      // Agregar la categoría a la lista local
      setCategories((prevCategories) => [data.category, ...prevCategories]);

      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Actualiza una categoría existente.
   *
   * @param {string} categoryWebId - ID web de la categoría
   * @param {Object} body - Datos actualizados de la categoría
   * @returns {Promise<Object>} Categoría actualizada
   */
  const updateCategory = useCallback(async (categoryWebId, body) => {
    const endpoint = `${ENV.SHOPS_BASE_URL}/api/v1/retailers/categories/${categoryWebId}`;
    const formData = fileUtils.objectToFormData({ category: body });

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {

          'MERCATELY-AUTH-TOKEN': ENV.MERCATELY_AUTH_TOKEN,

          'RETAILER-KEY': ENV.UNIQUE_KEY,
          Accept: 'application/json, text/plain, */*'
        },
        body: formData
      });

      const data = await handleRequestErrors(response);

      // Actualizar la categoría en la lista local
      setCategories((prevCategories) => prevCategories.map((category) => (category.web_id === categoryWebId ? { ...category, ...data.category } : category)));

      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Elimina una categoría.
   *
   * @param {string} categoryWebId - ID web de la categoría
   * @returns {Promise<Object>} Resultado de la eliminación
   */
  const deleteCategory = useCallback(async (categoryWebId) => {
    const endpoint = `${ENV.SHOPS_BASE_URL}/api/v1/retailers/categories/${categoryWebId}`;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',

          'MERCATELY-AUTH-TOKEN': ENV.MERCATELY_AUTH_TOKEN,

          'RETAILER-KEY': ENV.UNIQUE_KEY,
          'X-Access-Level': 'read-write'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Remover la categoría de la lista local
      setCategories((prevCategories) => prevCategories.filter((category) => category.web_id !== categoryWebId));

      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtiene opciones formateadas para dropdowns.
   *
   * @param {string} allLabel - Etiqueta para opción "Todas"
   * @returns {Array} Opciones formateadas con id y label
   */
  const getCategoryOptions = useCallback((allLabel = 'Todas las categorías') => {
    const allOption = { id: '', label: allLabel };
    const categoryOpts = categories.map((item) => ({
      id: item.web_id,
      label: item.name
    }));
    return [allOption, ...categoryOpts];
  }, [categories]);

  /**
   * Resetea el estado de categorías.
   */
  const resetCategories = useCallback(() => {
    setCategories([]);
    setError(null);
  }, []);

  /**
   * Limpia errores.
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // Estado
    categories,
    loading,
    error,

    // Acciones
    fetchCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    getCategoryOptions,
    resetCategories,
    clearError
  };
};

export default useCategoriesApi;
